import { useLang, useTextDomainContext, useUser } from "@/app/providers";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid";
import { exportableStatements } from "@/utils";
import {
  GENERATE_ENERGY_STATEMENT_FILE,
  useGetGroups,
  usePageTitle,
} from "@/hooks";
import { FilterBar1, ToolbarDropdown } from "@/components";
import { useNavigate } from "react-router";
import { useLazyQuery } from "@apollo/client/react";
import { enqueueSnackbar } from "notistack";
import { TfiImport } from "react-icons/tfi";

export const ClimateStatementExcel = () => {
  interface SelectedValuesPayload {
    energyType?: { value?: string };
    date?: { startDate?: Date; endDate?: Date };
  }

  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const navigate = useNavigate();
  const { user } = useUser();
  const lang = useLang();
  const isSuperAdmin = user?.roles?.includes("super-admin");

  // Set page title
  usePageTitle(gettext("Climate Report"));

  let { currentTeam: customer, currentSubsidiary: group } = user || {};

  if (!group) {
    group = "All";
  }

  const { subsidiary } = useGetGroups({ customerName: customer }) as any;

  //#region useState
  const [energyType, setEnergyType] = useState("electricity");
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setMonth(0, 1); // January 1st of current year
    return date.toISOString();
  });
  const [excelUrl, setExcelUrl] = useState<string | null>(null);
  const [sheets, setSheets] = useState<{ name: string; data: unknown[][] }[]>(
    [],
  );
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exportData, setExportData] = useState<
    { label: string; key: string; url?: string }[]
  >([]);

  const isAbortedRequestError = (error: any): boolean => {
    const abortIndicators = [
      error?.name,
      error?.code,
      error?.message,
      error?.cause?.name,
      error?.cause?.code,
      error?.cause?.message,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return (
      abortIndicators.includes("aborterror") ||
      abortIndicators.includes("aborted") ||
      abortIndicators.includes("operation was aborted") ||
      abortIndicators.includes("err_canceled") ||
      abortIndicators.includes("canceled") ||
      abortIndicators.includes("cancelled")
    );
  };

  // Keep a reference to the active worker so we can terminate it on cleanup.
  const workerRef = useRef<Worker | null>(null);
  //#endregion useState

  const [addExcelInQueue] = useLazyQuery(GENERATE_ENERGY_STATEMENT_FILE);

  //#region useEffect
  useEffect(() => {
    if (!customer) {
      navigate(`/user-account/profile`);
    }
  }, [customer, navigate]);

  // Build the Reports dropdown data and derive the Excel preview URL.
  useEffect(() => {
    const fetchData = async () => {
      const customerName = group.toLowerCase() === "all" ? customer : group;
      let fileNamePostfix = group.toLowerCase() === "all" ? "-all" : "";
      if (subsidiary.length < 2) {
        fileNamePostfix = "-all";
      }

      const res = await exportableStatements(
        "climate-statement",
        customerName,
        fileNamePostfix,
        energyType,
        fromDate,
        gettext,
      );

      const allItems: { label: string; key: string; url?: string }[] = [...res];

      if (isSuperAdmin) {
        allItems.push({
          label: gettext("Generate Climate Report"),
          key: "generate-report",
        });
      }

      setExportData(allItems as { label: string; key: string; url?: string }[]);

      const downloadItem = res.find((item) => item.key === "climate-report");
      setExcelUrl(downloadItem?.url ?? null);
    };

    fetchData();
  }, [
    customer,
    energyType,
    fromDate,
    gettext,
    group,
    isSuperAdmin,
    subsidiary.length,
  ]);

  // Offload the heavy XLSX + HyperFormula work to a Web Worker.
  useEffect(() => {
    if (!excelUrl) {
      // Defer state resets so they are not synchronous in the effect body.
      const id = setTimeout(() => {
        setSheets([]);
        setError(null);
      }, 0);
      return () => clearTimeout(id);
    }

    // Terminate any in-flight worker before starting a new one.
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }

    let active = true;

    const worker = new Worker(new URL("./excelWorker.ts", import.meta.url), {
      type: "module",
    });
    workerRef.current = worker;

    // Defer the initial setState calls so they are not synchronous in the
    // effect body (satisfies react-hooks/set-state-in-effect rule).
    Promise.resolve().then(() => {
      if (!active) return;
      setLoading(true);
      setError(null);
    });

    worker.onmessage = (
      event: MessageEvent<
        | { sheets: { name: string; data: unknown[][] }[] }
        | { error: string }
        | { aborted: boolean }
      >,
    ) => {
      if ("aborted" in event.data && event.data.aborted) {
        // Request was cancelled by navigation/filter changes.
      } else if ("error" in event.data) {
        setError(
          event.data.error ||
            gettext(
              "Unable to load the Climate Report. Please try again or contact support.",
            ),
        );
        setSheets([]);
      } else if ("sheets" in event.data) {
        setSheets(event.data.sheets);
        setActiveTab(0);
      }
      setLoading(false);
      worker.terminate();
      workerRef.current = null;
    };

    worker.onerror = (err) => {
      if (isAbortedRequestError(err)) {
        setLoading(false);
        worker.terminate();
        workerRef.current = null;
        return;
      }

      setError(
        err.message ||
          gettext(
            "Unable to load the Climate Report. Please try again or contact support.",
          ),
      );
      setSheets([]);
      setLoading(false);
      worker.terminate();
      workerRef.current = null;
    };

    // Send the URL to the worker – it fetches and parses everything there.
    worker.postMessage({ url: excelUrl, timestamp: Date.now() });

    // Terminate the worker if the component unmounts mid-load.
    return () => {
      active = false;
      worker.terminate();
      workerRef.current = null;
    };
  }, [excelUrl, gettext]);
  //#endregion useEffect

  //#region Handlers
  const handleGetSelectedValues = useCallback(
    (selectedValues: SelectedValuesPayload) => {
      if (selectedValues?.energyType?.value) {
        setEnergyType(selectedValues.energyType.value);
      }

      if (selectedValues?.date?.startDate) {
        setFromDate(selectedValues.date.startDate.toISOString());
      }
    },
    [],
  );
  const handleClickCustomDropdownOption = useCallback(
    (obj: { key: string }) => {
      let renamedGroup = group;
      if (subsidiary.length < 2) {
        renamedGroup = "all";
      }
      switch (obj.key) {
        case "generate-report":
          addExcelInQueue({
            variables: {
              meterType: energyType,
              customer: customer,
              group: renamedGroup,
              year: new Date(fromDate).getFullYear(),
              isClimate: true,
              languageCode: lang,
            },
          }).then(
            ({
              data,
            }: {
              data?: { generateEnergyStatementFile?: boolean };
            }) => {
              if (data?.generateEnergyStatementFile) {
                enqueueSnackbar(
                  gettext(
                    "The system is now creating the report, and you can download it shortly by clicking Download.",
                  ),
                  { variant: "success" },
                );
              } else {
                enqueueSnackbar(
                  gettext(
                    "An error occurred while the system was generating the report. Please try again. You can contact support If this continues.",
                  ),
                  { variant: "error" },
                );
              }
            },
          );
          break;
        default:
          break;
      }
    },
    [
      group,
      subsidiary.length,
      addExcelInQueue,
      energyType,
      customer,
      fromDate,
      gettext,
    ],
  );

  const postToolbar = useCallback(
    () => (
      <ToolbarDropdown
        data={exportData}
        label={gettext("Reports")}
        onClick={handleClickCustomDropdownOption}
        endIcon={<TfiImport size={14} />}
      />
    ),
    [exportData, gettext, handleClickCustomDropdownOption],
  );
  //#endregion Handlers

  //#region DataGrid conversion
  const activeSheetData = useMemo(
    () => sheets[activeTab]?.data ?? [],
    [sheets, activeTab],
  );

  const columns = useMemo<GridColDef[]>(() => {
    const headerRow = activeSheetData[0] ?? [];
    const dataRows = activeSheetData.slice(1) as unknown[][];
    return (headerRow as unknown[]).map((cell, colIndex) => {
      const isNumeric = dataRows.some(
        (row) => typeof (row as unknown[])[colIndex] === "number",
      );
      return {
        field: `col_${colIndex}`,
        headerName: String(cell ?? ""),
        flex: 1,
        minWidth: 120,
        sortable: false,
        align: isNumeric ? ("right" as const) : ("left" as const),
        headerAlign: isNumeric ? ("right" as const) : ("left" as const),
        renderCell: isNumeric
          ? (params) => (
              <div style={{ width: "100%", textAlign: "right" }}>
                {typeof params.value === "number" &&
                !Number.isInteger(params.value)
                  ? params.value.toFixed(2)
                  : String(params.value ?? "")}
              </div>
            )
          : undefined,
        valueFormatter: (value: unknown) => {
          if (typeof value === "number" && !Number.isInteger(value)) {
            return value.toFixed(2);
          }
          return value;
        },
      };
    });
  }, [activeSheetData]);

  const rows = useMemo(() => {
    return activeSheetData.slice(1).map((row, rowIndex) => {
      const obj: Record<string, unknown> = { id: rowIndex };
      (row as unknown[]).forEach((cell, colIndex) => {
        obj[`col_${colIndex}`] = cell ?? "";
      });
      return obj;
    });
  }, [activeSheetData]);
  //#endregion DataGrid conversion

  return (
    <>
      <FilterBar1
        getSelectedValues={handleGetSelectedValues}
        currentTeam={customer}
        currentSubsidiary={group}
        currentTeamId={user?.currentTeamId}
        currentSubsidiaryId={user?.currentSubsidiaryId}
        showCountry
        showBuilding
        showMeter
        showEnergyType
        showDateSingle
        showDateSingleHourly
        showDateSingleDaily
        showDateSingleMonthly
        showBuildingAll
        showMeterAll
      />
      <br />

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Typography sx={{ color: "text.secondary" }}>{error}</Typography>
        </Box>
      )}

      {!loading &&
        !error &&
        (!excelUrl || (sheets.length === 0 && excelUrl)) && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              mt: 4,
            }}
          >
            <Typography sx={{ color: "text.secondary" }}>
              {gettext("No Climate Report available for the selected filters.")}
            </Typography>
            {isSuperAdmin && (
              <Button
                variant="contained"
                onClick={() =>
                  handleClickCustomDropdownOption({ key: "generate-report" })
                }
              >
                {gettext("Generate Climate Report")}
              </Button>
            )}
          </Box>
        )}

      {!loading && sheets.length > 0 && (
        <Paper variant="outlined">
          {sheets.length > 1 && (
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
              variant="scrollable"
              scrollButtons="auto"
            >
              {sheets.map((sheet, index) => (
                <Tab key={index} label={sheet.name} />
              ))}
            </Tabs>
          )}

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", px: 1, pt: 0.5 }}
          >
            {postToolbar()}
          </Box>

          <DataGrid
            rows={rows}
            columns={columns}
            rowHeight={36}
            columnHeaderHeight={40}
            disableColumnMenu
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            pageSizeOptions={[5, 10, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 100, page: 0 } },
            }}
            sx={{
              border: 0,
              "& .MuiDataGrid-cell": { fontSize: "0.8rem" },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: "bold",
                fontSize: "0.8rem",
              },
            }}
          />
        </Paper>
      )}
    </>
  );
};
